/**
 * School admin → Classrooms.
 *
 * A classroom is a board, a grade and — optionally — a section, and it is what
 * every other relationship hangs off: enrolment is what makes a student a
 * teacher's student, and the subject a teacher holds HERE is what decides whose
 * paper they may set and whose results they may read. Its class teacher reads
 * every subject in it and looks after its roster.
 *
 * The board and grade lists come from the school's curriculum selection, not
 * from the enums. A school that has not said it runs Class 12 cannot create a
 * Class 12 classroom, and the absence propagates from one screen rather than
 * being re-checked on each. The subjects and units a teacher can be given are
 * narrowed the same way, to the topics the school ticked.
 *
 * The screen itself is components/admin/ClassroomsManager, shared with the
 * platform console — this file is the school's configuration of it: the school's
 * own staff, the school's own curriculum selection, sections and class
 * teachers, which a school has and CWY's own workspace does not.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import type { CurriculumSelectionDTO, TenantStaffMemberDTO } from '@cwy/config/contracts';
import { ClassroomsManager } from '@/components/admin/ClassroomsManager';

export function SchoolClassroomsPage() {
  const staff = useQuery({
    queryKey: ['tenant-admin', 'staff'],
    queryFn: () => apiGet<TenantStaffMemberDTO[]>('/tenant-admin/staff'),
  });
  const selection = useQuery({
    queryKey: ['tenant-admin', 'curriculum'],
    queryFn: () => apiGet<CurriculumSelectionDTO>('/tenant-admin/curriculum'),
  });
  // Empty until loaded, never null: null means "the whole tree", which a school
  // never has, and showing it for a moment invites a click on a subject the
  // school does not run.
  const offered = useMemo(() => new Set(selection.data?.chapterIds ?? []), [selection.data]);

  return (
    <ClassroomsManager
      // The year these classes are — after a rollover, last year's are
      // archived and these are the ones the import names.
      title={
        selection.data ? `Classrooms · ${selection.data.academicYear.label}` : 'Classrooms'
      }
      subtitle="A class is a board, a grade and a section. Assign teachers their subjects here — that is what gives them their students’ work."
      endpoint="/tenant-admin/classrooms"
      queryKey={['tenant-admin', 'classrooms']}
      cohorts={selection.data?.grades ?? []}
      cohortsLoaded={!selection.isPending}
      offeredChapterIds={offered}
      classTeachers
      classPanels
      teachers={(staff.data ?? [])
        .filter(
          (member) =>
            member.status === 'ACTIVE' &&
            (member.role === 'TEACHER' || member.role === 'COUNSELLOR'),
        )
        .map((member) => ({
          membershipId: member.membershipId,
          name: member.name,
          canBeClassTeacher: member.role === 'TEACHER',
        }))}
    />
  );
}
